import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import { Operation } from "rfc6902";
import {
    DefaultPagedResponse,
    Entity,
    Invoice,
    IUseQueryPaged,
    MutationResult,
    PagedResponse,
    QueryHookParams,
    QueryParams,
    QueryResult,
    QueryType,
    Surgery,
    SurgeryCalendarMetrics,
    SurgeryDashboardData,
    SurgeryFeeWaiverApprovalRequest,
} from "../models";
import { useAuth, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";

type AddImportTissueRequest = { surgeryId: number; tissueId: number };

interface IUseSurgeryQuery extends IUseQueryPaged<Surgery> {
    calendarMetricsQuery: QueryResult<SurgeryCalendarMetrics>;
    upsertMutation: MutationResult<Surgery, unknown, Surgery>;
    approveFeeWaiverMutation: MutationResult<Surgery, unknown, SurgeryFeeWaiverApprovalRequest>;
    approveCreateMutation: MutationResult<Surgery, unknown, number, void>;
    approveRescheduleMutation: MutationResult<Surgery, unknown, number, void>;
    surgeryDashboardQuery: QueryResult<SurgeryDashboardData>;
    invoiceQuery: QueryResult<Invoice | undefined>;
    addImportTissueMutation: MutationResult<Surgery, unknown, AddImportTissueRequest>;
}

interface IUseSurgeryQueryParams extends Omit<QueryHookParams, "queryType"> {
    queryType?: QueryType | "dashboard" | "calendar" | "invoice";
    includeCancelled?: boolean;
    surgeonId?: number;
    timeLow?: string;
    timeHigh?: string;
}

export const useSurgeryQuery = (params?: IUseSurgeryQueryParams): IUseSurgeryQuery => {
    const surgeryId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { get, post, patch, put, del } = useAxios();
    const url = Endpoints.Surgeries;
    const { isAuthorized } = useAuth();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Surgeries);

    const queryType = useMemo(() => {
        if (params?.queryType === "dashboard") return "dashboard";
        if (params?.queryType === "calendar") return "calendar";
        if (params?.queryType === "invoice") return "invoice";
        return getQueryType(surgeryId, params?.queryType);
    }, [params?.queryType, surgeryId]);

    const queryParamsWithArgs: QueryParams = {
        ...queryParams,
        surgeonId: params?.surgeonId?.toString() ?? "",
        timeLow: params?.timeLow ?? "",
        timeHigh: params?.timeHigh ?? "",
    };

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Surgery>>(
                getRequestOptions(url, config, queryParamsWithArgs, sortParams, pageParams, params?.includeCancelled)
            ),
        queryKey: queryKeys.surgeries.pagedList({ ...queryParamsWithArgs, ...pageParams, ...sortParams, ...params }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Surgery>>(
                getRequestOptions(
                    url,
                    config,
                    queryParamsWithArgs,
                    sortParams,
                    undefined,
                    params?.includeCancelled,
                    true
                )
            ).then((response) => response.items),
        queryKey: queryKeys.surgeries.list({ ...queryParamsWithArgs, ...sortParams, ...params }),
        placeholderData: [],
        enabled: isAuthorized && (queryType === QueryType.List || queryType === "calendar"),
        cacheTime: 5000,
        staleTime: 5000,
    });

    const calendarMetricsQuery = useGenericQuery({
        queryFn: (config) => get<SurgeryCalendarMetrics>({ url: `${url}/calendar-metrics`, config }),
        queryKey: queryKeys.surgeries.calendarMetrics(),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "calendar",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Surgery>({ url: `${url}/${surgeryId}`, config }),
        queryKey: queryKeys.surgeries.detail(surgeryId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const surgeryDashboardQuery = useGenericQuery({
        queryFn: (config) => get<SurgeryDashboardData>({ url: `${url}/dashboard`, config }),
        queryKey: queryKeys.dashboards.surgery(),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "dashboard",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const invoiceQuery = useGenericQuery({
        queryFn: (config) => get<Invoice | undefined>({ url: `${url}/${surgeryId}/invoice`, config }),
        queryKey: queryKeys.surgeries.invoice(surgeryId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "invoice",
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<Surgery>) => post<Partial<Surgery>, Surgery>({ url, data }),
        queryKey: queryKeys.surgeries.all(),
        keysToInvalidate: [
            queryKeys.surgeries.lists(),
            queryKeys.surgeries.pagedLists(),
            queryKeys.tissues.details(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
            queryKeys.dashboards.surgery(),
        ],
        successToast: "Surgery created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Surgery>({ url: `${url}/${surgeryId}`, data }),
        queryKey: queryKeys.surgeries.detail(surgeryId),
        keysToInvalidate: [
            queryKeys.surgeries.lists(),
            queryKeys.surgeries.pagedLists(),
            queryKeys.tissues.details(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
            queryKeys.dashboards.surgery(),
        ],
        successToast: "Surgery updated",
    });

    const upsertMutation = useGenericMutation({
        mutationFn: (data: Surgery) => put<Surgery>({ url, data }),
        queryKey: queryKeys.surgeries.detail(surgeryId),
        keysToInvalidate: [
            queryKeys.surgeries.lists(),
            queryKeys.surgeries.pagedLists(),
            queryKeys.tissues.details(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
            queryKeys.dashboards.surgery(),
        ],
        disableSuccessToast: true,
    });

    const approveFeeWaiverMutation = useGenericMutation({
        mutationFn: (data: SurgeryFeeWaiverApprovalRequest) =>
            put<SurgeryFeeWaiverApprovalRequest, Surgery>({ url: `${url}/approve-fee-waiver`, data }),
        queryKey: queryKeys.surgeries.detail(surgeryId),
        keysToInvalidate: [],
        successToast: "Surgery fee waiver approved",
    });

    const approveCreateMutation = useGenericMutation({
        mutationFn: (surgeryId: number) => put<void, Surgery>({ url: `${url}/${surgeryId}/approve-create` }),
        queryKey: queryKeys.surgeries.detail(surgeryId),
        keysToInvalidate: [
            queryKeys.surgeries.details(),
            queryKeys.surgeries.lists(),
            queryKeys.surgeries.pagedLists(),
            queryKeys.tissues.details(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
        ],
        successToast: "New surgery approved",
    });

    const approveRescheduleMutation = useGenericMutation({
        mutationFn: (surgeryId: number) => put<void, Surgery>({ url: `${url}/${surgeryId}/approve-reschedule` }),
        queryKey: queryKeys.surgeries.detail(surgeryId),
        keysToInvalidate: [
            queryKeys.surgeries.details(),
            queryKeys.surgeries.lists(),
            queryKeys.surgeries.pagedLists(),
            queryKeys.tissues.details(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
        ],
        successToast: "Surgery reschedule approved",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${surgeryId}` }),
        queryKey: queryKeys.surgeries.detail(surgeryId),
        keysToInvalidate: [queryKeys.surgeries.lists(), queryKeys.surgeries.pagedLists()],
        successToast: "Surgery cancelled",
    });

    const addImportTissueMutation = useGenericMutation({
        mutationFn: (data: AddImportTissueRequest) => post<AddImportTissueRequest, Surgery>({ url: `${url}/add-import-tissue`, data }),
        queryKey: queryKeys.surgeries.all(),
        keysToInvalidate: [
            queryKeys.surgeries.lists(),
            queryKeys.surgeries.pagedLists(),
            queryKeys.surgeries.details(),
            queryKeys.tissues.details(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
            queryKeys.dashboards.surgery(),
        ],
        successToast: "Tissue added to Surgery",
    });

    return useMemo<IUseSurgeryQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            calendarMetricsQuery,
            detailQuery,
            surgeryDashboardQuery,
            invoiceQuery,
            createMutation,
            updateMutation,
            upsertMutation,
            approveFeeWaiverMutation,
            approveCreateMutation,
            approveRescheduleMutation,
            deleteMutation,
            addImportTissueMutation,
        }),
        [
            pagedListQuery,
            listQuery,
            calendarMetricsQuery,
            detailQuery,
            surgeryDashboardQuery,
            invoiceQuery,
            createMutation,
            updateMutation,
            upsertMutation,
            approveFeeWaiverMutation,
            approveCreateMutation,
            approveRescheduleMutation,
            deleteMutation,
            addImportTissueMutation,
        ]
    );
};
